"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStore = exports.Store = exports.STORE_EXPIRATION_LIMIT = void 0;
const octopod_auth_1 = require("@produits-internes-oss/octopod-auth");
const react_1 = require("react");
const is_mobile_1 = require("is-mobile");
const object_1 = require("../../helpers/object/object");
const react_2 = require("../../helpers/react/react");
const useStore_entities_1 = require("./useStore.entities");
exports.STORE_EXPIRATION_LIMIT = 1000 * 3600 * 24 * 7;
exports.Store = (0, react_2.createContextWithHook)(({ entities }) => {
    const { connection } = (0, octopod_auth_1.useAuthenticator)();
    const { fetchFromOctopod } = (0, octopod_auth_1.useOctopod)();
    const [data, setData] = (0, react_1.useState)({});
    const [hasPrefetched, setHasPrefetched] = (0, react_1.useState)(false);
    const inflightRequests = (0, react_1.useRef)({});
    const getStatement = (0, react_1.useCallback)((key, action) => {
        const descriptor = Object.getOwnPropertyDescriptor(entities[key], action);
        return descriptor?.value;
    }, [entities]);
    const validate = (0, react_1.useCallback)((key) => {
        const validator = Object.getOwnPropertyDescriptor(entities[key], 'validate')?.value;
        if (!validator)
            return true;
        const raw = localStorage.getItem(key);
        if (!raw)
            return false;
        const items = Object.values(JSON.parse(raw));
        return items.length === 0 || validator(items[0]);
    }, [entities]);
    const hasCache = (0, react_1.useCallback)((key) => !(0, is_mobile_1.isMobile)() && localStorage.getItem(key) !== null && validate(key), [validate]);
    const getCache = (0, react_1.useCallback)((key) => {
        const items = Object.values(JSON.parse(localStorage.getItem(key)));
        return Object.fromEntries(items.map((item) => [item.id, (0, useStore_entities_1.createEntity)(entities[key], item.id, item)]));
    }, [entities]);
    const setCache = (0, react_1.useCallback)((key, storage) => {
        if (storage && !(0, is_mobile_1.isMobile)() && Object.getOwnPropertyDescriptor(entities[key], 'cache')?.value) {
            localStorage.setItem(key, JSON.stringify(Object.fromEntries(Object.entries(storage).map(([id, entity]) => [id, entity.attributes]))));
            localStorage.setItem(`${key}-expiration`, `${Date.now() + exports.STORE_EXPIRATION_LIMIT}`);
        }
    }, [entities]);
    const replaceInStorage = (0, react_1.useCallback)((key, storage) => setData((previous) => {
        const mutation = storage || {};
        setCache(key, mutation);
        return { ...previous, [key]: mutation };
    }), [setCache]);
    const mergeInStorage = (0, react_1.useCallback)((key, storage) => setData((previous) => {
        const mutation = { ...previous[key], ...(0, object_1.merge)(previous[key], storage) };
        setCache(key, mutation);
        return { ...previous, [key]: mutation };
    }), [setCache]);
    const removeFromStorage = (0, react_1.useCallback)((key, ids) => setData((previous) => {
        const mutation = (0, object_1.remove)(previous[key], ids);
        setCache(key, mutation);
        return { ...previous, [key]: mutation };
    }), [setCache]);
    const fetchStorage = (0, react_1.useCallback)(async (key, statement, parameters) => {
        const items = [];
        if (statement) {
            const { api, content, method, route, wrapper, transform } = statement(parameters);
            const cacheKey = `${method}:${api}:${route}`;
            if (!inflightRequests.current[cacheKey]) {
                inflightRequests.current[cacheKey] = Promise.resolve(fetchFromOctopod(method, route, { api, body: JSON.stringify(content), wrapper }))
                    .finally(() => { delete inflightRequests.current[cacheKey]; });
            }
            const response = await inflightRequests.current[cacheKey];
            if (response) {
                const resolved = transform ? transform(response) : (response && !Array.isArray(response) ? [response] : response);
                items.push(...resolved);
                return Object.fromEntries(items.map((item) => {
                    const attributes = { ...item, ...Object.getOwnPropertyDescriptor(item, 'attributes')?.value || {} };
                    delete attributes['attributes'];
                    return [item.id, (0, useStore_entities_1.createEntity)(entities[key], item.id, attributes)];
                }));
            }
        }
        return null;
    }, [entities, fetchFromOctopod]);
    const open = (0, react_1.useCallback)((key) => ({
        all: () => Object.values(data[key]),
        find: (id) => data[key][id],
        findAll: (ids) => ids.map((id) => data[key][id]),
        sort: (items) => items.sort(Object.getOwnPropertyDescriptor(entities[key], 'sort')?.value),
        where: (predicate) => Object.values(data[key]).filter(predicate),
    }), [data, entities]);
    const reference = (0, react_1.useCallback)(async (key, id, attributes, target) => {
        const statement = getStatement(key, target);
        const links = await fetchStorage(target, statement, { id, ...attributes });
        mergeInStorage(target, links);
    }, [getStatement, mergeInStorage, fetchStorage]);
    const refresh = (0, react_1.useCallback)(async (key, action, attributes) => {
        const statement = getStatement(key, action);
        const result = await fetchStorage(key, statement, { ...attributes });
        mergeInStorage(key, result);
    }, [getStatement, mergeInStorage, fetchStorage]);
    const create = (0, react_1.useCallback)(async (key, attributes) => {
        const statement = getStatement(key, 'create');
        mergeInStorage(key, await fetchStorage(key, statement, { ...attributes }));
    }, [getStatement, mergeInStorage, fetchStorage]);
    const update = (0, react_1.useCallback)(async (key, id, attributes) => {
        const statement = getStatement(key, 'update');
        const current = open(key).find(id);
        mergeInStorage(key, await fetchStorage(key, statement, { id, ...(0, object_1.merge)(current.attributes, attributes) }));
    }, [open, getStatement, mergeInStorage, fetchStorage]);
    const destroy = (0, react_1.useCallback)(async (key, id, attributes) => {
        const statement = getStatement(key, 'destroy');
        await fetchStorage(key, statement, { id, ...attributes });
        removeFromStorage(key, [id.toString()]);
    }, [getStatement, removeFromStorage, fetchStorage]);
    const triggerLoadStoragesOnMount = (0, react_1.useCallback)(async () => {
        if (connection.status === 'authorized' && !hasPrefetched) {
            Object.keys(localStorage).filter((k) => !k.endsWith('-expiration') && !(k in entities) && !k.startsWith('octopod-')).forEach((k) => { localStorage.removeItem(k); localStorage.removeItem(`${k}-expiration`); });
            Object.keys(entities).map((storage) => parseInt(localStorage.getItem(`${storage}-expiration`)) < Date.now() && localStorage.removeItem(storage));
            await Promise.all(Object.keys(entities).map(async (key) => {
                const statement = getStatement(key, 'all');
                if (hasCache(key)) {
                    setTimeout(async () => setCache(key, await fetchStorage(key, statement)));
                    return replaceInStorage(key, getCache(key));
                }
                return replaceInStorage(key, await fetchStorage(key, statement));
            }));
            setHasPrefetched(true);
        }
    }, [connection, entities, hasPrefetched, getStatement, hasCache, getCache, setCache, replaceInStorage, fetchStorage]);
    const triggerPurgeStoragesOnLogout = (0, react_1.useCallback)(async () => {
        if (connection.status === 'stale') {
            Object.keys(entities).forEach((entity) => {
                localStorage.removeItem(entity);
                localStorage.removeItem(`${entity}-expiration`);
            });
        }
    }, [connection, entities]);
    (0, react_1.useEffect)(() => { triggerLoadStoragesOnMount(); }, [triggerLoadStoragesOnMount]);
    (0, react_1.useEffect)(() => { triggerPurgeStoragesOnLogout(); }, [triggerPurgeStoragesOnLogout]);
    return (0, react_1.useMemo)(() => ({
        create,
        data,
        destroy,
        entities,
        hasPrefetched,
        open,
        reference,
        refresh,
        update,
    }), [
        create,
        data,
        destroy,
        entities,
        hasPrefetched,
        open,
        reference,
        refresh,
        update,
    ]);
});
const useStore = () => (0, react_1.useContext)(exports.Store.Context);
exports.useStore = useStore;
//# sourceMappingURL=useStore.js.map